import { defineStore } from 'pinia'
import { toRaw } from 'vue'
import type { Priority, Task } from '../../types/content'
import { useDatabase } from '../composables/useDatabase'
import { useEntityStore } from '../composables/useEntityStore'
import { tryCatch } from '../utils/result'

export const useTasksStore = defineStore('tasks', () => {
  const dbOps = useDatabase()

  const priorityWeight: Record<Exclude<Priority, null>, number> = {
    high: 0,
    medium: 1,
    low: 2
  }

  const {
    items: tasks,
    sortItems,
    getById,
    add,
    update,
    remove,
    restore,
    moveToContext
  } = useEntityStore<Task>({
    table: 'tasks',
    defaultSortBy: 'user',
    defaultSortOrder: 'asc',
    sortStrategies: {
      user: (a: Task, b: Task) => b.position - a.position,
      priority: (a: Task, b: Task) => {
        const weightA = a.priority ? priorityWeight[a.priority] : 999
        const weightB = b.priority ? priorityWeight[b.priority] : 999
        return weightA - weightB
      },
      title: (a: Task, b: Task) => b.title.localeCompare(a.title) as number
    }
  })

  const sortedPendingTasks = computed(() => {
    return sortItems(tasks.value.filter((n) => !n.completed))
  })

  const sortedCompletedTasks = computed(() => {
    return sortItems(tasks.value.filter((n) => n.completed))
  })

  const filteredTasks = computed(() => {
    return tasks.value
  })

  /**
   * Retrieves a task by its ID, ensuring labels array is initialized.
   * @param id - The task ID
   * @returns The task or null if not found
   */
  async function getTaskById(id: string) {
    const task = await getById(id)
    return task ? { ...task, labels: task.labels ?? [] } : null
  }

  /**
   * Adds a new task with the given payload in the current context.
   * @param payload - Partial task fields including title and optional dueDate
   * @returns The created task or null on failure
   */
  async function addNewTask(payload: Partial<Task>): Promise<Task | null> {
    return add({
      title: payload.title,
      dueDate: payload.dueDate || undefined,
      completed: false,
      labels: [],
      position: tasks.value.length
    } as Partial<Task>)
  }

  /**
   * Updates a task in the store and database by its ID with the provided changes.
   * @param task - An object containing the changes to be applied to the task.
   */
  async function updateTask(
    task: Partial<Task & { id: string }>
  ): Promise<void> {
    return update(task)
  }

  /**
   * Marks a task as deleted in the store and database by its ID.
   * @param id - The ID of the task to be deleted.
   */
  async function removeTask(id: string): Promise<void> {
    return remove(id)
  }

  /**
   * Restores a task that was previously marked as deleted.
   * @param id - The ID of the task to be restored.
   */
  async function restoreTask(id: string): Promise<void> {
    return restore(id)
  }

  /**
   * Moves tasks to a different context.
   * @param ids - Array of task IDs to move.
   * @param contextId - Target context ID.
   */
  async function moveTasksToAnotherContext(
    ids: string[],
    contextId: string
  ): Promise<void> {
    return moveToContext(ids, contextId)
  }

  /**
   * Updates the order of tasks in the store and database.
   * @param items - An array of Task objects representing the new order.
   */
  async function updateTaskOrder(items: Task[]): Promise<void> {
    const updatedTasks = items.map((task, index) => ({
      ...toRaw(task),
      position: index,
      updatedAt: Date.now()
    }))
    const result = await tryCatch(() =>
      Promise.all(updatedTasks.map((task) => dbOps.updateItem('tasks', task)))
    )
    if (!result.ok) {
      console.error('Failed to update task order:', result.error)
      return
    }
    const updatedMap = new Map(updatedTasks.map((t) => [t.id, t]))
    tasks.value = tasks.value.map((task) => updatedMap.get(task.id) ?? task)
  }

  return {
    tasks,
    sortedPendingTasks,
    sortedCompletedTasks,
    filteredTasks,
    getTaskById,
    addNewTask,
    updateTask,
    removeTask,
    restoreTask,
    moveTasksToAnotherContext,
    updateTaskOrder
  }
})
